import { Alert, Button, Collapse, Tooltip, Typography } from 'antd'
import { useFormikContext } from 'formik'
import { useMemo } from 'react'
import { useParams } from 'react-router'

import { Icon } from '@iconify/react'

import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { useAppSelector } from 'common/hooks/redux'
import { useFetchProceduresQuery } from 'features/Procedure/state/procedure.api'
import type {
  DynamicFieldValue,
  EmrCodeDynamicField,
} from 'features/Procedure/state/procedure-api.type'

import {
  flattenDynamicFields,
  getDynamicFieldName,
  isRenderableField,
} from '../components/manage-procedure/dynamic-fields.utils'
import type { FormValues } from '../components/manage-procedure/schema'
import type { PreviousTreatment, TreatmentFieldValue } from './previous-treatments.types'
import {
  coerceHistoricalValue,
  findLatestEncounterTreatments,
  isAppliedValue,
} from './previous-treatments.utils'

const { Text } = Typography

const GOLD = '#d9c179'
const GOLD_DARK = '#b89a45'

type ResolvedRow = {
  previousField: TreatmentFieldValue
  field: EmrCodeDynamicField | undefined
  coerced: DynamicFieldValue
  insertable: boolean
}

const isInsertableValue = (value: DynamicFieldValue): boolean =>
  value !== undefined && !(Array.isArray(value) && value.length === 0)

type Props = {
  disabled?: boolean
}

export const PreviousExtraData = ({ disabled = false }: Props) => {
  const { values, setValues } = useFormikContext<FormValues>()
  const { siteId } = useParams<{ siteId: string }>()
  const { setNotification } = useNotification()
  const selectedEncounterUid = useAppSelector(
    (state) => state.procedureReducer.selectedEncounterUid,
  )

  const { data: proceduresData, isError } = useFetchProceduresQuery(
    { siteId, encounterUid: selectedEncounterUid },
    { skip: !selectedEncounterUid || !siteId },
  )

  const selectedCodeId = values.cpt4_code?.id ?? null

  const treatments = useMemo(
    () =>
      findLatestEncounterTreatments(
        proceduresData?.historical_procedures,
        selectedEncounterUid,
        selectedCodeId,
      ),
    [proceduresData?.historical_procedures, selectedEncounterUid, selectedCodeId],
  )

  const formFields = useMemo(
    () => flattenDynamicFields(values.dynamic_fields).filter(isRenderableField),
    [values.dynamic_fields],
  )

  const fieldsByName = useMemo(() => {
    const map = new Map<string, EmrCodeDynamicField>()
    formFields.forEach((field) => map.set(getDynamicFieldName(field), field))
    return map
  }, [formFields])

  if (isError) {
    return (
      <div style={{ width: '100%', marginTop: 4 }}>
        <Alert type='error' showIcon message='Failed to load previous treatment data' />
      </div>
    )
  }

  if (!treatments.length) {
    return null
  }

  // the first dynamic input identifies the instance (e.g. Area) — shown in the
  // panel title as info instead of an insertable row
  const mainField = formFields[0]
  const mainFieldName = mainField ? getDynamicFieldName(mainField) : undefined

  const fieldValues = values.dynamic_field_values ?? {}

  const applyValues = (entries: ResolvedRow[]) => {
    void setValues((prev) => ({
      ...prev,
      dynamic_field_values: {
        ...(prev.dynamic_field_values ?? {}),
        ...Object.fromEntries(entries.map((row) => [row.previousField.name, row.coerced])),
      },
    }))
  }

  const insertOne = (row: ResolvedRow) => {
    applyValues([row])
    setNotification({
      type: ALERT_CONSTANTS.SUCCESS,
      duration: 2,
      title: 'Inserted',
      description: `Inserted “${row.field?.label ?? row.previousField.label}”`,
    })
  }

  const insertAll = (panelRows: ResolvedRow[]) => {
    const insertableRows = panelRows.filter((row) => row.insertable)
    if (!insertableRows.length) {
      setNotification({
        type: ALERT_CONSTANTS.WARNING,
        duration: 2,
        title: 'Nothing inserted',
        description: 'No previous values match the current procedure fields',
      })
      return
    }
    applyValues(insertableRows)
    const skipped = panelRows.length - insertableRows.length
    setNotification({
      type: ALERT_CONSTANTS.SUCCESS,
      duration: 2,
      title: 'Inserted',
      description:
        `Inserted ${insertableRows.length} values` +
        (skipped ? `, ${skipped} skipped (no matching field)` : ''),
    })
  }

  const insertTooltip = (row: ResolvedRow): string => {
    if (!row.field) {
      return 'No matching field in this procedure'
    }
    if (!row.insertable) {
      return 'Value format not supported for this field'
    }
    return ''
  }

  const renderRow = (row: ResolvedRow, index: number) => {
    const { previousField } = row
    const applied = row.insertable && isAppliedValue(row.coerced, fieldValues[previousField.name])
    const label = row.field?.label ?? previousField.label

    return (
      <div
        key={previousField.name}
        style={{
          display: 'grid',
          gridTemplateColumns: '180px 1fr auto',
          alignItems: 'center',
          gap: 16,
          padding: '11px 14px',
          borderTop: index === 0 ? 'none' : '1px solid #f0f0f0',
          background: applied ? '#f6fbf1' : 'transparent',
        }}>
        <span style={{ fontSize: 12.5, fontWeight: 500, color: '#8c8c8c' }}>{label}</span>
        <span style={{ fontSize: 14, fontWeight: 500, color: '#1f2937', overflowWrap: 'anywhere' }}>
          {previousField.value}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 'none' }}>
          {applied ? (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                color: '#52c41a',
                fontSize: 12,
                fontWeight: 600,
                padding: '0 6px',
              }}>
              <Icon icon='mdi:check' width={14} height={14} />
              Inserted
            </span>
          ) : (
            <Tooltip title={insertTooltip(row)}>
              {/* span keeps the tooltip working while the button is disabled */}
              <span>
                <Button
                  size='small'
                  disabled={disabled || !row.insertable}
                  onClick={() => insertOne(row)}
                  style={{
                    color: GOLD_DARK,
                    borderColor: GOLD,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                  }}
                  icon={<Icon icon='mdi:tray-arrow-down' width={14} height={14} />}>
                  Insert
                </Button>
              </span>
            </Tooltip>
          )}
        </div>
      </div>
    )
  }

  const buildPanel = (treatment: PreviousTreatment) => {
    const mainInfo = mainFieldName
      ? treatment.fields.find((field) => field.name === mainFieldName)
      : undefined

    const rows: ResolvedRow[] = treatment.fields.map((previousField) => {
      const field = fieldsByName.get(previousField.name)
      const coerced = field ? coerceHistoricalValue(field, previousField.raw) : undefined
      return { previousField, field, coerced, insertable: !!field && isInsertableValue(coerced) }
    })
    const hasInsertable = rows.some((row) => row.insertable)

    const label = (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.4, minWidth: 0 }}>
          <span style={{ fontWeight: 600, color: '#374151' }}>
            Previous Extra Data for{' '}
            <span style={{ color: GOLD_DARK }}>
              {treatment.procedure}
              {mainInfo ? ` (${mainField?.label ?? mainInfo.label}: ${mainInfo.value})` : ''}
            </span>
          </span>
          <Text type='secondary' style={{ fontSize: 12 }}>
            Last recorded {treatment.date} — insert only the values you need
          </Text>
        </div>
        <div style={{ marginLeft: 'auto', flex: 'none' }}>
          <Button
            size='small'
            onClick={(event) => {
              event.stopPropagation()
              insertAll(rows)
            }}
            disabled={disabled || !hasInsertable}
            style={{ background: GOLD, borderColor: GOLD, color: '#3d3418', fontWeight: 600 }}>
            Insert all
          </Button>
        </div>
      </div>
    )

    const body = rows.length ? (
      <div
        style={{
          border: '1px solid #f0f0f0',
          borderRadius: 8,
          overflow: 'hidden',
          background: '#fff',
        }}>
        {rows.map(renderRow)}
      </div>
    ) : (
      <Text type='secondary' style={{ fontSize: 12 }}>
        No additional values recorded
      </Text>
    )

    return { key: treatment.id, label, children: body }
  }

  return (
    <div style={{ width: '100%', marginTop: 4 }}>
      <Collapse expandIconPosition='end' items={treatments.map(buildPanel)} />
    </div>
  )
}
